<template>
    <teleport to="body">
        <div
            v-if="isAppLoaderVisible"
            class="loader-overlay"
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="loader-title"
            aria-describedby="loader-desc"
            tabindex="-1"
            ref="dialog"
        >
            <div class="loader-content">
                <h2 id="loader-title" class="sr-only">
                    {{ $t('Loading in progress') }}
                </h2>
                <p id="loader-desc" class="sr-only">
                    {{ $t('Please wait') }}
                </p>

                <p class="loading-text">
                    {{ loadingText || $t('Loading in progress') }}
                </p>

                <div class="wrapper">
                    <div class="progress-container">
                        <div
                            class="progress-bar"
                            :class="{
                                indeterminate: showIndeterminate,
                            }"
                            :style="!showIndeterminate ? { width: appLoaderProgress + '%' } : {}"
                        />
                    </div>

                    <div class="progress-text" v-if="!showIndeterminate">
                        {{ appLoaderProgress }}%
                    </div>
                </div>
            </div>
        </div>
    </teleport>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from 'vue'
import { useLoaderStore } from '@/stores/loader.ts'
import { storeToRefs } from 'pinia'

const { appLoaderProgress, isAppLoaderVisible, loadingText } = storeToRefs(useLoaderStore())

const dialog = ref<HTMLElement | null>(null)

const trapFocus = (e: KeyboardEvent) => {
    if (e.key === 'Tab') {
        e.preventDefault()
        dialog.value?.focus()
    }
}
const showIndeterminate = computed(() => {
    return appLoaderProgress.value === 0 || appLoaderProgress.value === 100
})

const clearListener = () => {
    document.removeEventListener('keydown', trapFocus)
}

onBeforeUnmount(() => {
    clearListener()
})

watch(
    isAppLoaderVisible,
    (active) => {
        if (active) {
            document.body.style.overflow = 'hidden'
            document.addEventListener('keydown', trapFocus)

            requestAnimationFrame(() => {
                dialog.value?.focus()
            })

            document.querySelector('#app')?.setAttribute('aria-hidden', 'true')
        } else {
            clearListener()

            document.body.style.overflow = ''
            document.querySelector('#app')?.removeAttribute('aria-hidden')
        }
    },
    { immediate: true },
)
</script>

<style scoped lang="scss">
.loader-overlay {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.55);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 9998;

    .sr-only {
        position: absolute;
        width: 1px;
        height: 1px;
        padding: 0;
        margin: -1px;
        overflow: hidden;
        clip: rect(0, 0, 0, 0);
        white-space: nowrap;
        border: 0;
    }

    .loader-content {
        display: flex;
        width: 240px;
        flex-direction: column;
        gap: $space-md;
        padding: $space-md;
        background-color: var(--app-loader-background);
        border-radius: $space-md;
        box-shadow:
            0 4px 6px rgba(0, 0, 0, 0.3),
            0 1px 3px rgba(0, 0, 0, 0.2);
    }

    .loading-text {
        text-align: center;
    }

    .progress-container {
        width: 100%;
        height: 10px;
        background: var(--app-loader-progress-backround);
        border-radius: 4px;
        overflow: hidden;
        position: relative;
    }

    .wrapper {
        display: flex;
        flex-direction: row;
        align-items: center;
        gap: 8px;
        height: 26px;
    }

    .progress-bar {
        height: 100%;
        background: var(--color-primary-active);
        transition: width 0.2s ease;
    }

    .progress-text {
        white-space: nowrap;
        color: inherit;
    }

    .progress-bar.indeterminate {
        position: absolute;
        width: 30%;
        border-radius: 4px;
        animation: loading 1s infinite linear;
    }

    @keyframes loading {
        0% {
            left: -30%;
        }
        100% {
            left: 100%;
        }
    }
}
</style>
